import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Document, SubscriptionPlan } from '@vi-iot/client';

import {
  BookingStore,
  BookingSubscriptionsPlanByNames,
  DocumentsStore,
  SelectedInstallationsPlatforms,
} from '@store';

@Component({
  selector: 'app-booking-summary',
  templateUrl: './booking-summary.component.html',
  styleUrls: ['./booking-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingSummaryComponent implements OnInit, OnDestroy {
  @Input() anyInstallationSelected: boolean;
  @Input() isCheckout: boolean;
  @Output() continue = new EventEmitter<void>();

  plan: SubscriptionPlan;
  bookingPlans: BookingSubscriptionsPlanByNames;
  BookingPlans = SubscriptionPlan;
  tosAccepted = false;
  document: Document;
  selectedInstallationsQuantityAndPrice: SelectedInstallationsPlatforms;
  finalPrice: string;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public bookingStore: BookingStore,
    private documentsStore: DocumentsStore,
    private cdr: ChangeDetectorRef,
    private decimalPipe: DecimalPipe
  ) {}

  ngOnInit(): void {
    this.handleSubscriptions();
    this.documentsStore.getDocuments();
  }

  private handleSubscriptions(): void {
    this.bookingStore.selectedPlan$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((plan) => {
      this.plan = plan;
    });

    this.documentsStore.termsOfUseDocument$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((document) => {
        this.document = document;
        this.cdr.detectChanges();
      });

    this.bookingStore.getSelectedInstallationsPlatformsWithPriceData$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data) => {
        this.selectedInstallationsQuantityAndPrice = data.installations;
        this.bookingPlans = data.plans;
        this.calculateFinalPrice(data.installations);
        this.cdr.detectChanges();
      });
  }

  onTosCheckboxChange(isTosAccepted: boolean): void {
    this.tosAccepted = isTosAccepted;
  }

  isContinueDisabled(): boolean {
    if (this.document) {
      const tosNotAcceptedOnCheckout = this.isCheckout && !this.tosAccepted;

      return tosNotAcceptedOnCheckout || !this.anyInstallationSelected;
    } else {
      return this.isCheckout || !this.anyInstallationSelected;
    }
  }

  // TBD Such data should be returned from the backend
  private calculateFinalPrice(installations: SelectedInstallationsPlatforms): void {
    const e3Quantity = installations.e3Devices.length;
    const legacyQuantity = installations.legacyDevices.length;
    let finalPrice = 0;
    let currency: string;

    if (this.plan === SubscriptionPlan.Plus) {
      finalPrice =
        e3Quantity * this.bookingPlans.plus.price.price +
        legacyQuantity * this.bookingPlans.plusLegacy.price.price;
      currency = this.bookingPlans.plus.price.currency;
    } else if (this.plan === SubscriptionPlan.Pro) {
      finalPrice =
        e3Quantity * this.bookingPlans.pro.price.price +
        legacyQuantity * this.bookingPlans.proLegacy.price.price;
      currency = this.bookingPlans.pro.price.currency;
    }
    const fixedFinalPrice = this.decimalPipe.transform(finalPrice.toFixed(2), '1.2-2', 'de-DE');

    this.finalPrice = `${fixedFinalPrice} ${currency}`;
    this.cdr.detectChanges();
  }

  onContinue(): void {
    this.continue.emit();
  }

  onConfirmTrialVersion(): void {
    this.bookingStore.createTrialSubscription();
    this.continue.emit();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.bookingStore.resetSubscriptionsDetails();
  }
}
